import { useMemo, useState } from 'react';
import { X, Search, Loader2, MapPin, Check } from 'lucide-react';
import { useGetMedicalLabsQuery, useMedicalAssignLabMutation, useCsAssignLabMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { AssignLabResponse, MedicalLab } from '@/types';

interface AssignLabModalProps {
    isOpen: boolean;
    onClose: () => void;
    investigationId: string;
    // Which role's assign-lab route to call.
    variant: 'medical' | 'customer-success';
    // Employee location (e.g. employmentTown) used to seed the search and shorten the list.
    locationHint?: string | null | undefined;
    // The currently assigned lab (drives the "Reassign" title and a "Current" tag).
    currentLabId?: string | null | undefined;
    onAssigned: (result: AssignLabResponse) => void;
}

export default function AssignLabModal({
    isOpen,
    onClose,
    investigationId,
    variant,
    locationHint,
    currentLabId,
    onAssigned,
}: AssignLabModalProps) {
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState('');
    const [error, setError] = useState('');

    // Search across all labs when the user types; otherwise seed the search with
    // the employee's location to keep the default list short.
    const effectiveSearch = search.trim() || (locationHint ?? '').trim();
    const filters = effectiveSearch ? { search: effectiveSearch } : undefined;
    const { data: labs = [], isLoading, isFetching } = useGetMedicalLabsQuery(filters, { skip: !isOpen });

    const [assignMedical, { isLoading: isAssigningMedical }] = useMedicalAssignLabMutation();
    const [assignCs, { isLoading: isAssigningCs }] = useCsAssignLabMutation();
    const isAssigning = isAssigningMedical || isAssigningCs;

    // Group labs by region for sectioned rendering.
    const grouped = useMemo(() => {
        const map = new Map<string, MedicalLab[]>();
        labs.forEach((lab) => {
            const key = lab.region || lab.state || 'Other';
            const list = map.get(key) ?? [];
            list.push(lab);
            map.set(key, list);
        });
        return Array.from(map.entries());
    }, [labs]);

    const handleAssign = async () => {
        if (!selectedId) return;
        setError('');
        try {
            const args = { id: investigationId, labId: selectedId };
            const result = await (variant === 'customer-success' ? assignCs(args) : assignMedical(args)).unwrap();
            onAssigned(result);
            onClose();
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to assign the lab.'));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">
                            {currentLabId ? 'Reassign medical lab' : 'Assign medical lab'}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            The employee is emailed the lab + their test panel automatically.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-100">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name, area, city or address"
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                        />
                    </div>
                    {!search.trim() && locationHint && (
                        <p className="text-[11px] text-gray-400 mt-2">
                            Showing labs near {locationHint}. Search to widen.
                        </p>
                    )}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading || isFetching ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
                        </div>
                    ) : labs.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-12">
                            No labs found. Try a different search.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {grouped.map(([region, regionLabs]) => (
                                <div key={region} className="flex flex-col gap-2">
                                    <p className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                                        {region}
                                    </p>
                                    {regionLabs.map((lab) => {
                                        const isSelected = selectedId === lab.id;
                                        const isCurrent = currentLabId === lab.id;
                                        const locationLine = [lab.address, lab.city, lab.state]
                                            .filter(Boolean)
                                            .join(', ');
                                        return (
                                            <button
                                                key={lab.id}
                                                type="button"
                                                onClick={() => setSelectedId(lab.id)}
                                                className={`text-left rounded-xl border p-3 transition-colors ${
                                                    isSelected
                                                        ? 'border-[#FF6B35] bg-[#FF6B35]/5'
                                                        : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="min-w-0">
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            {lab.name}
                                                            {isCurrent && (
                                                                <span className="ml-2 text-[10px] uppercase tracking-wider text-[#FF6B35]">
                                                                    Current
                                                                </span>
                                                            )}
                                                        </p>
                                                        {locationLine && (
                                                            <p className="text-xs text-gray-500 mt-0.5 flex items-start gap-1">
                                                                <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gray-400" />
                                                                {locationLine}
                                                            </p>
                                                        )}
                                                    </div>
                                                    {isSelected && (
                                                        <span className="shrink-0 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6B35]">
                                                            <Check className="w-3.5 h-3.5 text-white" />
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex flex-col gap-2">
                    {error && <p className="text-xs text-red-600">{error}</p>}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isAssigning}
                            className="flex-1 border border-gray-200 hover:bg-gray-50 text-gray-700 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleAssign}
                            disabled={!selectedId || isAssigning}
                            className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                        >
                            {isAssigning && <Loader2 className="w-4 h-4 animate-spin" />}
                            {currentLabId ? 'Reassign lab' : 'Assign lab'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
